import { captureException } from '@sentry/react';
import { StateCreator } from 'zustand';

import { memberService } from '@/services/database';
import {
    appendEntity,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { publishLocalDeltaPacket } from '@/stores/realtime';
import { Member } from '@/types';

import { MemberActions, MemberState } from '../types';

let memberListRequestVersion = 0;

export const createMemberActions: StateCreator<
    MemberState & MemberActions,
    [['zustand/immer', never]],
    [],
    MemberActions
> = (set, get) => ({
    // Core
    fetchMembers: async (params) => {
        const requestVersion = ++memberListRequestVersion;

        set({ loading: true });

        try {
            const members = await memberService.list(params);

            if (requestVersion === memberListRequestVersion) {
                set({ members, loading: false, error: undefined });
            }
        } catch (error) {
            captureException(error);

            if (requestVersion === memberListRequestVersion) {
                set({ loading: false, error: 'Failed to load members' });
            }
        }
    },
    fetchActiveMember: async () => {
        set((state) => {
            state.loadingMember = true;
            state.error = undefined;
        });

        try {
            const member = await memberService.getActive();

            set({
                selectedMember: member,
                loadingMember: false,
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch member detail';
                state.loadingMember = false;
            });
        }
    },
    fetchMemberById: async (id) => {
        set((state) => {
            state.loadingMember = true;
            state.error = undefined;
        });

        try {
            const member = await memberService.getById(id);

            set({
                selectedMember: member,
                loadingMember: false,
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch member detail';
                state.loadingMember = false;
            });
        }
    },
    clearSelectedMember: () => {
        set((state) => {
            state.selectedMember = null;
        });
    },
    createMember: async (input) => {
        const tempId = `temp-${Date.now()}`;
        const optimistic = { id: tempId, ...input } as Member;

        set({ members: appendEntity(get().members, optimistic) });

        publishLocalDeltaPacket({
            v: 1,
            type: 'delta:packet',
            entity: 'member',
            operation: 'create',
            entityId: tempId,
            payload: optimistic,
            timestamp: Date.now(),
        });

        try {
            const member = await memberService.create(input);

            set({ members: replaceEntityById(get().members, tempId, member) });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'member',
                operation: 'delete',
                entityId: tempId,
                timestamp: Date.now(),
            });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'member',
                operation: 'create',
                entityId: member.id,
                payload: member,
                timestamp: Date.now(),
            });

            return member;
        } catch (error) {
            captureException(error);

            set({ members: removeEntityById(get().members, tempId), error: 'Failed to create member' });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'member',
                operation: 'delete',
                entityId: tempId,
                timestamp: Date.now(),
            });
        }
    },
    updateMember: async (id, updates) => {
        const prev = get().members.find((f) => f.id === id);
        if (!prev) {
            return;
        }
        const prevSnapshot = { ...prev };

        set({
            members: mergeEntityById(get().members, id, updates),
            selectedMember:
                get().selectedMember?.id === id ? mergeEntity(get().selectedMember!, updates) : get().selectedMember,
        });

        publishLocalDeltaPacket({
            v: 1,
            type: 'delta:packet',
            entity: 'member',
            operation: 'update',
            entityId: id,
            payload: updates,
            timestamp: Date.now(),
        });

        try {
            const member = await memberService.update(id, updates);

            set({
                members: mergeEntityById(get().members, id, member),
                selectedMember:
                    get().selectedMember?.id === id ? mergeEntity(get().selectedMember!, member) : get().selectedMember,
            });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'member',
                operation: 'update',
                entityId: id,
                payload: member,
                timestamp: Date.now(),
            });
        } catch (error) {
            captureException(error);

            set({
                members: replaceEntityById(get().members, id, prevSnapshot),
                selectedMember: get().selectedMember?.id === id ? prevSnapshot : get().selectedMember,
                error: 'Failed to update member',
            });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'member',
                operation: 'update',
                entityId: id,
                payload: prevSnapshot,
                timestamp: Date.now(),
            });

            throw error;
        }
    },
    updateMemberPermission: async (id, permission) => {
        const prev = get().members.find((f) => f.id === id);
        if (!prev) {
            return;
        }
        const prevSnapshot = { ...prev };

        set({
            members: mergeEntityById(get().members, id, { permission }),
            selectedMember:
                get().selectedMember?.id === id
                    ? mergeEntity(get().selectedMember!, { permission })
                    : get().selectedMember,
        });

        publishLocalDeltaPacket({
            v: 1,
            type: 'delta:packet',
            entity: 'member',
            operation: 'update',
            entityId: id,
            payload: { permission },
            timestamp: Date.now(),
        });

        try {
            const member = await memberService.updatePermission(id, permission);

            set({
                members: mergeEntityById(get().members, id, member),
                selectedMember:
                    get().selectedMember?.id === id ? mergeEntity(get().selectedMember!, member) : get().selectedMember,
            });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'member',
                operation: 'update',
                entityId: id,
                payload: member,
                timestamp: Date.now(),
            });
        } catch (error) {
            captureException(error);

            set({
                members: replaceEntityById(get().members, id, prevSnapshot),
                selectedMember: get().selectedMember?.id === id ? prevSnapshot : get().selectedMember,
                error: 'Failed to update member permission',
            });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'member',
                operation: 'update',
                entityId: id,
                payload: prevSnapshot,
                timestamp: Date.now(),
            });

            throw error;
        }
    },
    deleteMember: async (id) => {
        const prev = [...get().members];
        const deleted = structuredClone(get().members.find((f) => f.id === id));

        set({
            members: removeEntityById(get().members, id),
            selectedMember: get().selectedMember?.id === id ? null : get().selectedMember,
        });

        publishLocalDeltaPacket({
            v: 1,
            type: 'delta:packet',
            entity: 'member',
            operation: 'delete',
            entityId: id,
            timestamp: Date.now(),
        });

        try {
            await memberService.delete(id);
        } catch (error) {
            captureException(error);

            set({ members: prev, error: 'Failed to delete member' });

            if (deleted) {
                publishLocalDeltaPacket({
                    v: 1,
                    type: 'delta:packet',
                    entity: 'member',
                    operation: 'create',
                    entityId: id,
                    payload: deleted,
                    timestamp: Date.now(),
                });
            }

            throw error;
        }
    },
    applyMemberDelta: (packet) => {
        if (packet.entity !== 'member') {
            return;
        }

        const next = reduceEntityDelta<Member>(get().members, get().selectedMember, packet, {
            prependCreated: true,
        });

        if (!next) {
            return;
        }

        set({
            members: next.items,
            selectedMember: next.selected,
        });
    },
});
